import type { FC } from 'react'
import { useRouter } from 'next/router'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next'
import { WishlistDropdown } from '../../WishlistDropdown'
import { useCopyProductsBetweenWishlists } from '../../Hooks/use-copy-products-between-wishlists'
import { Maybe, WishlistItemInterface, Wishlist } from '@pylot-data/pylotschema'
import { getWishlistItems } from '../../utils/getWishlistItems'
import { WishlistState } from '../../Hooks/WishlistActionTypes'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import s from '@pagestyles/Wishlist.module.scss'

export type Props = {
    item?: Maybe<WishlistItemInterface>
    wishlist: Wishlist
    wishlistState?: WishlistState
    dropDownLabel: string
}

export const CopyWishlist: FC<Props> = ({
    item,
    wishlist,
    wishlistState,
    dropDownLabel
}) => {
    const { t } = useTranslation(['wishlist'])
    const { openToast } = useUI()
    const router = useRouter()
    const {
        isLoading,
        copyProductsBetweenWishlists
    } = useCopyProductsBetweenWishlists()
    const config = useStoreConfig()

    const handleCopyEvent = async (id: number) => {
        if (wishlistState && !wishlistState?.selected.length) {
            openToast(
                t('wishlist|Please select items to copy.'),
                ToastType.Warning
            )
            return false
        }
        try {
            const response = await copyProductsBetweenWishlists({
                sourceWishlistUid: String(wishlist.id),
                destinationWishlistUid: String(id),
                wishlistItems: getWishlistItems({
                    wishlist,
                    item,
                    wishlistState
                })
            })

            if (response?.errors?.length) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }

            if (
                response?.data?.copyProductsBetweenWishlists
                    ?.destination_wishlist
            ) {
                const {
                    id: routeId,
                    name
                } = response?.data?.copyProductsBetweenWishlists?.destination_wishlist

                router.push(`/account/wishlist/${routeId}`)

                openToast(
                    t('wishlist|{{ product }} was copied to {{ wishlist }}.', {
                        wishlist: name,
                        product: item?.product?.name || 'Product'
                    }),
                    ToastType.Success
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    if (!config.base.wishlist.multiple_wishlists) return null

    return (
        <div className={s['copy-wishlist-link']}>
            <WishlistDropdown
                isLoading={isLoading}
                onClick={handleCopyEvent}
                excludeIds={[wishlist?.id!]}
                openDisplayLabel={dropDownLabel}
                closeDisplayLabel={dropDownLabel}
                onCreate={handleCopyEvent}
            />
        </div>
    )
}
